import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { generateId } from '../data/mockData';
import {
  Upload,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  X,
  FileText,
  Building2,
  ChevronDown,
  Package,
  Hash,
  StickyNote,
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

// ─── CSV Parser ───────────────────────────────────────────────────────────────

function parseCSV(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      row.push(cell.trim());
      cell = '';
    } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && next === '\n') i++;
      row.push(cell.trim());
      if (row.some(c => c !== '')) rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell || row.length > 0) {
    row.push(cell.trim());
    if (row.some(c => c !== '')) rows.push(row);
  }
  return rows;
}

// ─── Column definitions ───────────────────────────────────────────────────────

const REQUIRED_FIELDS = [
  { key: 'Mark',        label: 'Door Mark / ID',    hint: 'e.g. D.01, MG03' },
  { key: 'ToRoom',      label: 'Room / Description', hint: 'e.g. ENTRY, OFFICE' },
  { key: 'Code',        label: 'Supplier Code',      hint: 'External item number' },
  { key: 'Description', label: 'Product Description',hint: '' },
  { key: 'Product',     label: 'Legend Code',        hint: 'e.g. HI-1, LO-53' },
  { key: 'Qty',         label: 'Quantity',            hint: '' },
];

const OPTIONAL_FIELDS = [
  { key: 'Level',        label: 'Level / Floor',       default: true  },
  { key: 'Building',     label: 'Building',             default: true  },
  { key: 'Height',       label: 'Door Height (mm)',     default: true  },
  { key: 'Width',        label: 'Door Width (mm)',      default: true  },
  { key: 'Thickness',    label: 'Door Thickness (mm)',  default: false },
  { key: 'DoorMaterial', label: 'Door Material',        default: false },
  { key: 'FrameMaterial',label: 'Frame Material',       default: false },
  { key: 'Handing',      label: 'Door Handing',         default: false },
  { key: 'Brand',        label: 'Product Brand',        default: true  },
  { key: 'Finish',       label: 'Product Finish',       default: false },
  { key: 'DoorNotes',    label: 'Door Notes',           default: true  },
  { key: 'SetNotes',     label: 'Hardware Set Notes',   default: false },
];

// ─── Door structure builder ───────────────────────────────────────────────────

function buildDoorStructure(dataRows, columnMap, includeFields, allProducts) {
  const get = (row, field) => {
    const idx = columnMap[field];
    return idx !== undefined ? (row[idx] || '').trim() : '';
  };

  const doorMap = new Map(); // key → door object
  const order = [];           // preserve insertion order

  for (const row of dataRows) {
    const mark = get(row, 'Mark');
    if (!mark) continue;

    const buildingName = (includeFields.has('Building') && get(row, 'Building')) || 'Building 1';
    const levelName    = (includeFields.has('Level') && get(row, 'Level')) || 'Ground Floor';
    const doorKey      = `${buildingName}||${levelName}||${mark}`;

    if (!doorMap.has(doorKey)) {
      const entry = {
        mark,
        toRoom:      get(row, 'ToRoom') || mark,
        buildingName,
        levelName,
        height:      includeFields.has('Height')       ? get(row, 'Height')       : '',
        width:       includeFields.has('Width')        ? get(row, 'Width')        : '',
        thickness:   includeFields.has('Thickness')    ? get(row, 'Thickness')    : '',
        material:    includeFields.has('DoorMaterial') ? get(row, 'DoorMaterial') : '',
        frameMat:    includeFields.has('FrameMaterial')? get(row, 'FrameMaterial'): '',
        handing:     includeFields.has('Handing')      ? get(row, 'Handing')      : '',
        notes:       '',
        setNotes:    '',
        products:    [],
      };
      doorMap.set(doorKey, entry);
      order.push(doorKey);
    }

    const door = doorMap.get(doorKey);

    // Door notes — collect unique, non-empty notes
    if (includeFields.has('DoorNotes')) {
      const n = get(row, 'DoorNotes');
      if (n && !door.notes) door.notes = n;
    }
    if (includeFields.has('SetNotes')) {
      const sn = get(row, 'SetNotes');
      if (sn && !door.setNotes) door.setNotes = sn;
    }

    // Product line
    const code       = get(row, 'Code');
    const legendCode = get(row, 'Product');
    const qty        = Math.max(1, parseInt(get(row, 'Qty')) || 1);
    const description= get(row, 'Description');
    const brand      = includeFields.has('Brand')  ? get(row, 'Brand')  : '';
    const finish     = includeFields.has('Finish') ? get(row, 'Finish') : '';

    if (code || legendCode) {
      const existing = allProducts.find(p =>
        (p.legendCode && legendCode && p.legendCode === legendCode) ||
        (p.supplierCode && code && p.supplierCode === code)
      );
      door.products.push({
        code, legendCode, description, brand, finish, qty,
        existingProductId: existing?.id || null,
        isNew: !existing,
      });
    }
  }

  // Build buildings→floors→doors map
  const buildings = new Map();
  for (const key of order) {
    const door = doorMap.get(key);
    if (!buildings.has(door.buildingName)) buildings.set(door.buildingName, new Map());
    const floors = buildings.get(door.buildingName);
    if (!floors.has(door.levelName)) floors.set(door.levelName, []);
    floors.get(door.levelName).push(door);
  }

  return { doorMap, buildings, order };
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ImportScheduleWizard({ onClose }) {
  const { state, actions, products } = useApp();
  const allProducts = useMemo(
    () => [...products, ...(state.importedProducts || [])],
    [products, state.importedProducts]
  );

  const fileInputRef = useRef(null);
  const [step, setStep]             = useState(1);
  const [fileName, setFileName]     = useState('');
  const [dataRows, setDataRows]     = useState([]);
  const [availableCols, setAvailableCols] = useState([]);
  const [columnMap, setColumnMap]   = useState({});
  const [includeFields, setIncludeFields] = useState(
    () => new Set(OPTIONAL_FIELDS.filter(f => f.default).map(f => f.key))
  );
  const [appendMode, setAppendMode] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [expandedDoors, setExpandedDoors] = useState(new Set());

  // ── File handling ──────────────────────────────────────────────────────────

  const processFile = useCallback((file) => {
    if (!file || !file.name.match(/\.csv$/i)) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const allRows = parseCSV(e.target.result);
      if (allRows.length < 3) return;

      const headerRow = allRows[1]; // row 2 = machine field names
      setAvailableCols(headerRow);

      const map = {};
      [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS].forEach(({ key }) => {
        const idx = headerRow.findIndex(h => h.trim() === key);
        if (idx !== -1) map[key] = idx;
      });
      setColumnMap(map);
      setDataRows(allRows.slice(2));
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    processFile(e.dataTransfer.files[0]);
  }, [processFile]);

  // ── Parsed structure ───────────────────────────────────────────────────────

  const parsed = useMemo(() => {
    if (!dataRows.length || !Object.keys(columnMap).length) return null;
    return buildDoorStructure(dataRows, columnMap, includeFields, allProducts);
  }, [dataRows, columnMap, includeFields, allProducts]);

  const stats = useMemo(() => {
    if (!parsed) return null;
    let doors = 0, totalProducts = 0, newProds = 0;
    for (const [, door] of parsed.doorMap) {
      doors++;
      door.products.forEach(p => { totalProducts++; if (p.isNew) newProds++; });
    }
    return {
      buildings: parsed.buildings.size,
      floors: [...parsed.buildings.values()].reduce((s, f) => s + f.size, 0),
      doors,
      products: totalProducts,
      matched: totalProducts - newProds,
      newProds,
    };
  }, [parsed]);

  // ── Toggle helpers ─────────────────────────────────────────────────────────

  const toggleField = (key) => {
    setIncludeFields(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleDoor = (key) => {
    setExpandedDoors(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // ── Import execution ───────────────────────────────────────────────────────

  const handleImport = () => {
    if (!parsed) return;
    const { doorMap, buildings } = parsed;

    // Collect new products
    const newProducts = [];
    const newProdMap = new Map(); // key → generated id

    for (const [, door] of doorMap) {
      for (const p of door.products) {
        if (!p.isNew) continue;
        const key = p.legendCode || p.code;
        if (!newProdMap.has(key)) {
          const prod = {
            id: generateId(),
            legendCode: p.legendCode,
            supplierCode: p.code,
            name: p.description || p.legendCode || p.code,
            type: 'imported',
            price: 0,
            brand: p.brand,
            finish: p.finish,
            compatibleDoorTypes: ['standard', 'exit', 'fire-rated'],
            description: p.description,
          };
          newProducts.push(prod);
          newProdMap.set(key, prod.id);
        }
      }
    }

    // Build buildings → floors → doors
    const builtBuildings = [];
    for (const [buildingName, floors] of buildings) {
      const builtFloors = [];
      for (const [levelName, doors] of floors) {
        const builtDoors = doors.map(door => {
          const productIds = [];
          for (const p of door.products) {
            const productId = p.existingProductId || newProdMap.get(p.legendCode || p.code);
            if (productId) {
              for (let i = 0; i < p.qty; i++) productIds.push(productId);
            }
          }
          return {
            id: generateId(),
            name: `${door.mark} - ${door.toRoom}`,
            type: 'standard',
            notes: [door.notes, door.setNotes].filter(Boolean).join(' | '),
            products: productIds,
            attributes: {
              width: parseInt(door.width) || 800,
              height: parseInt(door.height) || 2100,
              thickness: door.thickness || '',
              material: door.material || '',
              frameMaterial: door.frameMat || '',
              handing: door.handing || '',
            },
          };
        });
        builtFloors.push({ id: generateId(), name: levelName, doors: builtDoors });
      }
      builtBuildings.push({ id: generateId(), name: buildingName, floors: builtFloors });
    }

    actions.importSchedule({ buildings: builtBuildings, newProducts, appendMode });
    onClose();
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const missingRequired = REQUIRED_FIELDS.filter(f => columnMap[f.key] === undefined);
  const canProceed = dataRows.length > 0 && missingRequired.length === 0;

  // ── UI ─────────────────────────────────────────────────────────────────────

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-muted/10">

      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b border-border px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Upload className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium text-sm">Import Door Schedule from CSV</span>
          <div className="flex items-center gap-1">
            <StepIndicator n={1} active={step === 1} done={step > 1} label="Configure" />
            <ChevronRight className="w-3 h-3 text-muted-foreground" />
            <StepIndicator n={2} active={step === 2} done={false} label="Preview & Import" />
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4 mr-1" />
          Cancel
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-6 max-w-4xl">

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <div className="space-y-6">

              {/* Drop zone */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">1. Upload CSV File</h3>
                <div
                  className={cn(
                    'border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors',
                    isDragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
                    fileName && 'border-green-400 bg-green-50/50'
                  )}
                  onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={e => processFile(e.target.files[0])}
                  />
                  {fileName ? (
                    <div className="flex flex-col items-center gap-2">
                      <CheckCircle2 className="w-10 h-10 text-green-500" />
                      <p className="font-medium text-sm">{fileName}</p>
                      <p className="text-xs text-muted-foreground">{dataRows.length} product rows parsed</p>
                      <p className="text-xs text-primary underline">Click to replace</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Upload className="w-10 h-10 opacity-40" />
                      <p className="font-medium text-sm">Drag & drop your CSV file here</p>
                      <p className="text-xs">or click to browse — supports ASSA and similar formats</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Column mapping (only shown once file is loaded) */}
              {dataRows.length > 0 && (
                <>
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">2. Column Mapping</h3>
                    <div className="bg-white border border-border rounded-lg divide-y divide-border">
                      <div className="grid grid-cols-3 gap-4 px-4 py-2 bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        <span>App Field</span>
                        <span>CSV Column</span>
                        <span>Status</span>
                      </div>
                      {REQUIRED_FIELDS.map(({ key, label, hint }) => {
                        const detected = columnMap[key] !== undefined;
                        const colName = detected ? availableCols[columnMap[key]] : undefined;
                        return (
                          <div key={key} className="grid grid-cols-3 gap-4 px-4 py-2.5 items-center">
                            <div>
                              <p className="text-sm font-medium">{label}</p>
                              {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
                            </div>
                            <select
                              className="text-sm border border-border rounded px-2 py-1 bg-white w-full"
                              value={columnMap[key] !== undefined ? columnMap[key] : ''}
                              onChange={e => {
                                const v = e.target.value;
                                setColumnMap(prev => v === '' ? { ...prev, [key]: undefined } : { ...prev, [key]: parseInt(v) });
                              }}
                            >
                              <option value="">— not mapped —</option>
                              {availableCols.map((col, idx) => (
                                <option key={idx} value={idx}>{col || `Column ${idx + 1}`}</option>
                              ))}
                            </select>
                            <div>
                              {detected ? (
                                <span className="flex items-center gap-1 text-xs text-green-600">
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                  <span>Auto-detected: <code className="bg-green-100 px-1 rounded">{colName}</code></span>
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-xs text-amber-600">
                                  <AlertCircle className="w-3.5 h-3.5" />
                                  Required — please map
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  {/* Optional fields */}
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">3. Optional Fields to Import</h3>
                    <div className="bg-white border border-border rounded-lg p-4 grid grid-cols-2 gap-2">
                      {OPTIONAL_FIELDS.map(({ key, label }) => {
                        const detected = columnMap[key] !== undefined;
                        return (
                          <label key={key} className={cn('flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-muted/50', !detected && 'opacity-50')}>
                            <input
                              type="checkbox"
                              checked={includeFields.has(key)}
                              onChange={() => toggleField(key)}
                              className="accent-primary"
                              disabled={!detected}
                            />
                            <span>{label}</span>
                            {!detected && <span className="text-xs text-muted-foreground">(not in file)</span>}
                          </label>
                        );
                      })}
                    </div>
                  </section>

                  {/* Import mode */}
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">4. Import Mode</h3>
                    <div className="bg-white border border-border rounded-lg p-4 space-y-2">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="radio" name="mode" checked={!appendMode} onChange={() => setAppendMode(false)} className="mt-0.5 accent-primary" />
                        <div>
                          <p className="text-sm font-medium">Replace all buildings in this quote</p>
                          <p className="text-xs text-muted-foreground">Existing buildings/floors/doors will be removed and replaced with the imported data.</p>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="radio" name="mode" checked={appendMode} onChange={() => setAppendMode(true)} className="mt-0.5 accent-primary" />
                        <div>
                          <p className="text-sm font-medium">Append to existing buildings</p>
                          <p className="text-xs text-muted-foreground">Imported buildings will be added alongside any existing ones.</p>
                        </div>
                      </label>
                    </div>
                  </section>

                  {missingRequired.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>Please map the following required columns before continuing: {missingRequired.map(f => f.label).join(', ')}</span>
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end pt-2">
                <Button disabled={!canProceed} onClick={() => setStep(2)}>
                  Preview Import
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && parsed && stats && (
            <div className="space-y-5">

              {/* Summary bar */}
              <div className="grid grid-cols-5 gap-3">
                {[
                  { label: 'Buildings', value: stats.buildings, icon: Building2 },
                  { label: 'Floors', value: stats.floors, icon: Hash },
                  { label: 'Doors', value: stats.doors, icon: FileText },
                  { label: 'Products matched', value: stats.matched, icon: CheckCircle2, green: true },
                  { label: 'New products', value: stats.newProds, icon: AlertCircle, amber: stats.newProds > 0 },
                ].map(({ label, value, icon: Icon, green, amber }) => (
                  <div key={label} className={cn(
                    'bg-white border rounded-lg p-3 text-center',
                    green && value > 0 ? 'border-green-200' : amber && value > 0 ? 'border-amber-200' : 'border-border'
                  )}>
                    <Icon className={cn('w-4 h-4 mx-auto mb-1', green && value > 0 ? 'text-green-600' : amber && value > 0 ? 'text-amber-600' : 'text-muted-foreground')} />
                    <p className="text-lg font-semibold leading-none">{value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{label}</p>
                  </div>
                ))}
              </div>

              {/* New products notice */}
              {stats.newProds > 0 && (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    <strong>{stats.newProds} new product{stats.newProds !== 1 ? 's' : ''}</strong> will be added to your product catalog with a price of $0.00 — you can update pricing later.
                  </span>
                </div>
              )}

              {/* Door preview grouped by building → floor */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Door Preview</h3>
                <div className="space-y-4">
                  {[...parsed.buildings.entries()].map(([buildingName, floors]) => (
                    <div key={buildingName} className="bg-white border border-border rounded-lg overflow-hidden">
                      {/* Building header */}
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-border">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-semibold">{buildingName}</span>
                        <span className="text-xs text-muted-foreground">({floors.size} floor{floors.size !== 1 ? 's' : ''})</span>
                      </div>

                      {[...floors.entries()].map(([levelName, doors]) => (
                        <div key={levelName}>
                          {/* Floor sub-header */}
                          <div className="flex items-center gap-2 px-4 py-1.5 bg-muted/20 border-b border-border">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{levelName}</span>
                            <span className="text-xs text-muted-foreground">— {doors.length} door{doors.length !== 1 ? 's' : ''}</span>
                          </div>

                          {/* Door rows */}
                          {doors.map((door) => {
                            const doorKey = `${buildingName}||${levelName}||${door.mark}`;
                            const isExpanded = expandedDoors.has(doorKey);
                            const newCount = door.products.filter(p => p.isNew).length;
                            return (
                              <div key={doorKey} className="border-b border-border last:border-0">
                                <button
                                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 text-left transition-colors"
                                  onClick={() => toggleDoor(doorKey)}
                                >
                                  <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform shrink-0', !isExpanded && '-rotate-90')} />
                                  <span className="text-sm font-mono font-medium w-16 shrink-0">{door.mark}</span>
                                  <span className="text-sm text-muted-foreground flex-1 truncate">{door.toRoom}</span>
                                  {door.notes && <StickyNote className="w-3.5 h-3.5 text-amber-500 shrink-0" title={door.notes} />}
                                  <span className="text-xs text-muted-foreground shrink-0">{door.products.length} item{door.products.length !== 1 ? 's' : ''}</span>
                                  {newCount > 0 && (
                                    <Badge className="bg-amber-100 text-amber-700 text-[10px] shrink-0">{newCount} new</Badge>
                                  )}
                                </button>

                                {isExpanded && (
                                  <div className="px-10 pb-3 space-y-1">
                                    {door.notes && (
                                      <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mb-2">
                                        <span className="font-medium">Note:</span> {door.notes}
                                      </p>
                                    )}
                                    {door.products.length === 0 && (
                                      <p className="text-xs text-muted-foreground italic">No products</p>
                                    )}
                                    {door.products.map((p, i) => (
                                      <div key={i} className="flex items-center gap-2 text-xs">
                                        {p.isNew ? (
                                          <AlertCircle className="w-3 h-3 text-amber-500 shrink-0" title="New product — will be added to catalog" />
                                        ) : (
                                          <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" title="Matched existing product" />
                                        )}
                                        <code className="bg-muted px-1 rounded text-[11px] shrink-0">{p.legendCode || p.code}</code>
                                        <span className="text-muted-foreground truncate">{p.description}</span>
                                        {p.brand && <span className="text-muted-foreground/60 shrink-0">{p.brand}</span>}
                                        <span className="ml-auto shrink-0 font-medium">×{p.qty}</span>
                                        {p.isNew && <Badge className="bg-amber-100 text-amber-700 text-[10px] shrink-0">new</Badge>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </section>

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">
                    {appendMode ? 'Will append to existing buildings' : 'Will replace existing buildings'}
                  </span>
                  <Button onClick={handleImport}>
                    <Package className="w-4 h-4 mr-2" />
                    Import {stats.doors} Doors
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({ n, active, done, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={cn(
        'w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors',
        active ? 'bg-primary text-primary-foreground' :
        done   ? 'bg-green-500 text-white' :
                 'bg-muted text-muted-foreground'
      )}>
        {done ? '✓' : n}
      </div>
      <span className={cn('text-xs', active ? 'font-medium text-foreground' : 'text-muted-foreground')}>{label}</span>
    </div>
  );
}
